import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

// Circuit parameters data
const circuitParametersData = [
  {
    parameter: 'R0',
    value: 0.0042,
    minValue: 0.002,
    maxValue: 0.01,
    typical: 0.0035,
    unit: 'Ohm',
    explanation: 'Electrolyte resistance'
  },
  {
    parameter: 'R1',
    value: 0.012,
    minValue: 0.002,
    maxValue: 0.05,
    typical: 0.015,
    unit: 'Ohm',
    explanation: 'SEI layer resistance'
  },
  {
    parameter: 'C1',
    value: 0.0028,
    minValue: 0.001,
    maxValue: 0.01,
    typical: 0.003,
    unit: 'Farad',
    explanation: 'SEI layer capacitance'
  },
  {
    parameter: 'R2',
    value: 0.018,
    minValue: 0.005,
    maxValue: 0.05,
    typical: 0.02,
    unit: 'Ohm',
    explanation: 'Charge-transfer resistance'
  },
  {
    parameter: 'WO1',
    value: 0.012,
    minValue: 0.001,
    maxValue: 0.05,
    typical: 0.01,
    unit: '-',
    explanation: 'Warburg impedance'
  },
  {
    parameter: 'C2',
    value: 0.093,
    minValue: 0.01,
    maxValue: 0.5,
    typical: 0.1,
    unit: 'Farad',
    explanation: 'Double-layer capacitance'
  }
];

// Styled Typography component
const StyledTypography = ({ children, ...props }) => (
  <Typography
    sx={{
      fontFamily: 'Bai Jamjuree',
      color: '#fff',
      ...props.sx
    }}
    {...props}
  >
    {children}
  </Typography>
);

// Visual indicator component
const VisualIndicator = ({ current, min, max }) => {
  const percentage = ((current - min) / (max - min)) * 100;
  
  return (
    <Box sx={{ 
      width: '100%', 
      height: '20px', 
      backgroundColor: 'rgba(232, 68, 45, 0.1)', 
      borderRadius: '10px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Box sx={{
        position: 'absolute',
        left: `${percentage}%`,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '8px',
        height: '8px',
        backgroundColor: '#e8442d',
        borderRadius: '50%',
        border: '2px solid #fff',
        zIndex: 2
      }} />
      <Box sx={{
        width: `${percentage}%`,
        height: '100%',
        backgroundColor: 'rgba(232, 68, 45, 0.3)',
        borderRadius: '10px'
      }} />
    </Box>
  );
};

// Table cell styles
const cellStyles = {
  header: {
    color: '#e8442d',
    fontWeight: 700,
    borderRight: '1px solid rgba(232, 68, 45, 0.2)',
    fontFamily: 'Bai Jamjuree',
    fontSize: '1rem'
  },
  data: {
    color: '#fff',
    fontWeight: 500,
    borderRight: '1px solid rgba(232, 68, 45, 0.2)',
    fontFamily: 'Bai Jamjuree',
    fontSize: '1rem'
  },
  visualIndicator: {
    padding: '8px',
    fontFamily: 'Bai Jamjuree'
  }
};

const CircuitParametersTable = () => {
  return (
    <Paper elevation={3} sx={{
      background: 'transparent',
      border: '2px solid #e8442d',
      borderRadius: '1rem',
      mt: 10,
      p: 3
    }}>
      <StyledTypography variant="h6" sx={{ 
        color: '#e8442d', 
        mb: 6, 
        textAlign: 'center', 
        fontFamily: 'Bai Jamjuree',
        fontWeight: 700 
      }}>
        Equivalent Circuit Model Parameters
      </StyledTypography>

      <Box sx={{
        border: '1px solid rgba(232, 68, 45, 0.2)',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        background: 'rgba(0,0,0,0.3)',
        fontFamily: 'Bai Jamjuree'
      }}>
        <TableContainer>
          <Table sx={{ fontFamily: 'Bai Jamjuree' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(232, 68, 45, 0.1)' }}>
                <TableCell sx={cellStyles.header}>Parameter</TableCell>
                <TableCell sx={cellStyles.header}>Value</TableCell>
                <TableCell sx={cellStyles.header}>Min Value</TableCell>
                <TableCell sx={cellStyles.header}>Max Value</TableCell>
                <TableCell sx={cellStyles.header}>Typical</TableCell>
                <TableCell sx={cellStyles.header}>Unit</TableCell>
                <TableCell sx={cellStyles.header}>Explanation</TableCell>
                <TableCell sx={{ ...cellStyles.header, borderRight: 'none' }}>Visual Indicator</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {circuitParametersData.map((param, index) => (
                <TableRow 
                  key={param.parameter}
                  sx={{ borderBottom: '1px solid rgba(232, 68, 45, 0.2)' }}
                >
                  <TableCell sx={cellStyles.data}>{param.parameter}</TableCell>
                  <TableCell sx={cellStyles.data}>{param.value}</TableCell>
                  <TableCell sx={cellStyles.data}>{param.minValue}</TableCell>
                  <TableCell sx={cellStyles.data}>{param.maxValue}</TableCell>
                  <TableCell sx={cellStyles.data}>{param.typical}</TableCell>
                  <TableCell sx={cellStyles.data}>{param.unit}</TableCell>
                  <TableCell sx={cellStyles.data}>{param.explanation}</TableCell>
                  <TableCell sx={cellStyles.visualIndicator}>
                    <VisualIndicator 
                      current={param.value} 
                      min={param.minValue} 
                      max={param.maxValue} 
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
};

export default CircuitParametersTable;
